import { clsx, type ClassValue } from "clsx";
import { SSE } from "sse.js";
import { twMerge } from "tailwind-merge";

export const BASE_URL =
  (process.env.NEXT_PUBLIC_BASE_URL ||
    "https://carepilot-server.eurekahealth.com/") + "api/v2";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const apiFetch = async <T = any>(
  uri: string,
  sessionToken?: string,
  options?: Omit<RequestInit, "body"> & {
    body?: Record<string, any> | string | FormData | URLSearchParams;
  } // overriding the body type so we can also accept an object to JSON-ize
): Promise<T> => {
  const url = `${BASE_URL}${uri}`;
  const headers = new Headers(options?.headers);

  // Set default headers for JSON content
  headers.set("Content-Type", "application/json");
  headers.set("Accept", "application/json");

  if (sessionToken) {
    headers.set("Authorization", `Bearer ${sessionToken}`);
  }

  const body = options?.body
    ? typeof options.body === "string"
      ? options.body
      : JSON.stringify(options.body)
    : undefined;

  const response = await fetch(url, {
    ...options,
    method: options?.method || "POST", // Default to POST if not specified
    headers,
    body,
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(
      JSON.stringify({
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        body: errorBody,
        message: `API request failed: ${response.status} ${response.statusText}`,
      })
    );
  }

  return response.json();
};

interface SSEConfig {
  uri: string;
  sessionToken?: string;
  headers?: Record<string, string>;
  method?: string;
  payload?: any;
  onlyCallOnMessageWhenChanged?: boolean;
  onMessage: (data: any) => void;
  onError?: (error: any) => void;
}

export class SSEManager {
  private sse: SSE | null = null;
  private lastMessage: any = null;

  startStreaming(config: SSEConfig) {
    const defaultHeaders = {
      "Content-Type": "application/json",
      ...(config.sessionToken
        ? { Authorization: `Bearer ${config.sessionToken}` }
        : {}),
    };

    const headers = {
      ...defaultHeaders,
      ...config.headers,
    };

    const defaultOnError = (error: Event) => {
      console.error("SSE failed:", error);
    };

    const onError = config.onError || defaultOnError;

    this.sse = new SSE(`${BASE_URL}${config.uri}`, {
      headers,
      method: config.method,
      payload: JSON.stringify(config.payload),
    });

    this.sse.addEventListener("message", (event: MessageEvent) => {
      if (
        config.onlyCallOnMessageWhenChanged &&
        event.data === this.lastMessage
      ) {
        return;
      }

      this.lastMessage = event.data;

      try {
        const parsedData = JSON.parse(event.data);
        config.onMessage(parsedData);
      } catch (error) {
        console.error("Error parsing SSE message:", error);
      }
    });

    this.sse.addEventListener("error", (error: Event) => {
      console.error("SSE error event:", error);
      onError(error);
    });

    this.sse.addEventListener("readystatechange", (event: any) => {
      if (event.readyState === event.source.CLOSED) {
        this.stopStreaming();
      }
    });

    this.sse.stream();
  }

  stopStreaming() {
    if (this.sse) {
      this.sse.close();
      this.sse = null;
    }
  }
}

const sendTextToSlack = async (text: string): Promise<boolean> => {
  // Don't send messages to Slack in development
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "localhost"
  ) {
    console.log("Skipping Slack message in development:", text);
    return true;
  }

  const url =
    "https://carepilot-server.hypothetical.ly/api/care/send_text_to_slack";
  const headers = new Headers();
  headers.append("Content-type", "application/json");

  try {
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      console.error(
        `Failed to send message to Slack, status: ${response.status}`
      );
      return false; // Indicate failure
    }
    return true; // Indicate success
  } catch (error) {
    console.error("Error sending text to Slack:", error);
    return false; // Indicate failure
  }
};

export default sendTextToSlack;
